import { useCallback, useRef } from 'react';

import { getStepId } from '@/features/guided-financial-planning/config';
import { useSession, useSessionMutations } from '@/features/guided-financial-planning/context';
import { useWizardRouter } from './use-wizard-router';

/**
 * Hook that combines URL routing with automatic step completion tracking.
 * When the user navigates forward, it marks the previous step as complete in the database.
 */
export function useWizardStepSync() {
  const { startIndex, onStepChange: navigateToStep } = useWizardRouter();
  const { session } = useSession();
  const { updateStep } = useSessionMutations();
  const previousIndexRef = useRef(startIndex);

  const onStepChange = useCallback(
    (newStepIndex: number) => {
      const previousIndex = previousIndexRef.current;
      const isForward = newStepIndex > previousIndex;

      // Mark previous step as complete on forward navigation
      if (isForward && session?.status !== 'completed') {
        const newStepId = getStepId(newStepIndex);
        updateStep(newStepId, true); // marks previous step as complete
      }

      navigateToStep(newStepIndex);
      previousIndexRef.current = newStepIndex;
    },
    [session?.status, updateStep, navigateToStep],
  );

  return { startIndex, onStepChange };
}
